import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { addHours, addMinutes, isBefore, isFuture, isPast } from 'date-fns';
import { ReactNode } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import TimeLeft from '@sorare/core/src/contexts/ticker/TimeLeft';
import { sortBy, uniqueBy } from '@sorare/core/src/lib/arrays';
import { glossary } from '@sorare/core/src/lib/glossary';

import InlineGame from 'components/stats/InlineGame';

import {
  FixtureTimeline_so5Fixture,
  FixtureTimeline_so5Lineup,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ gap: 0.5 })`
  padding: var(--double-unit);
  /* Add 1px to the dot width to center the line */
  --dot-width: calc(var(--unit) + 1px);
`;

const Dot = styled.div`
  grid-area: dot;
  width: var(--dot-width);
  height: var(--dot-width);
  border-radius: 50%;
  border: 1px solid var(--c-neutral-500);
  box-sizing: border-box;
  .completed & {
    background: var(--c-neutral-500);
  }
`;

const VerticalLine = styled.div`
  grid-area: line;
  justify-self: center;
  width: 1px;
  background: var(--c-neutral-400);
  min-height: var(--quadruple-unit);
  margin-top: calc(-1 * var(--intermediate-unit));
`;

const TimelineRowWrapper = styled.div`
  display: grid;
  grid-template-areas:
    'dot title date'
    'line description date';
  &.isGameRow {
    grid-template-areas:
      'dot title date'
      'line description description';
  }
  column-gap: var(--unit);
  grid-template-columns: var(--dot-width) 1fr max-content;
  align-items: stretch;
  border-radius: var(--unit);
  &:last-child {
    & ${VerticalLine} {
      display: none;
    }
  }
  & ${Dot}, ${VerticalLine} {
    transform: translateY(var(--unit));
  }
`;

const DateColumn = styled(Vertical).attrs({ gap: 0 })`
  grid-area: date;
  margin-left: var(--quadruple-unit);
  padding-top: 2px;
  align-items: flex-end;
  text-transform: uppercase;
`;

const GameWrapper = styled.div`
  & > * {
    margin: var(--unit) 0;
    border-radius: var(--unit);
  }
  & * {
    background: var(--c-neutral-400);
    width: 100%;
  }
`;

const RowLabel = styled(BodyM)`
  color: var(--c-neutral-600);
  .completed & {
    color: var(--c-neutral-500);
  }
  .isRightAfterNow & {
    color: var(--c-neutral-1000);
  }
`;

const Description = styled(LabelS)`
  grid-area: description;
  color: var(--c-neutral-700);
  .isRightAfterNow & {
    color: var(--c-neutral-1000);
  }
`;

const NowLabel = styled.span`
  text-transform: capitalize;
`;

type TimelineRowProps = {
  label: ReactNode;
  extraInfo?: ReactNode;
  date?: Date;
  isGameRow?: boolean;
};

const ROUNDED_MINUTES = [0, 15, 30, 45, 60];
const TimelineRow = ({
  label,
  extraInfo,
  date,
  isRightAfterNow,
  isGameRow,
}: TimelineRowProps & {
  isRightAfterNow?: boolean;
}) => {
  // sometimes API returns date with :29 or :59 minutes
  // let's round it up before display
  const roundedMinutesDate = ROUNDED_MINUTES.includes(
    (date?.getMinutes() || 0) + 1
  )
    ? addMinutes(date!, 1)
    : date;
  return (
    <TimelineRowWrapper
      className={classNames({
        completed: !date || isPast(date),
        isRightAfterNow,
        isGameRow,
      })}
    >
      <Dot />
      <VerticalLine />
      <RowLabel bold>{label}</RowLabel>

      <Description>
        {isRightAfterNow && date ? (
          <FormattedMessage
            id="FixtureTimeline.displayedDate"
            defaultMessage="in {timeLeft}"
            values={{ timeLeft: <TimeLeft time={date} /> }}
          />
        ) : (
          extraInfo
        )}
      </Description>
      {date && !isGameRow && (
        <DateColumn>
          <BodyS bold color="var(--c-neutral-700)">
            <FormattedDate value={date} day="numeric" month="short" />
          </BodyS>
          <LabelS color="var(--c-neutral-700)">
            <FormattedDate
              value={roundedMinutesDate}
              hour="numeric"
              minute="numeric"
            />
          </LabelS>
        </DateColumn>
      )}
    </TimelineRowWrapper>
  );
};

const TimelineRowWithNow = ({
  isRightAfterNow,
  nowTimeline,
  ...otherProps
}: TimelineRowProps & {
  isRightAfterNow: boolean;
  nowTimeline: ReactNode;
}) => {
  return (
    <>
      {isRightAfterNow && nowTimeline}
      <TimelineRow {...otherProps} isRightAfterNow={isRightAfterNow} />
    </>
  );
};

type Props = {
  so5Fixture: FixtureTimeline_so5Fixture;
  so5Lineup?: FixtureTimeline_so5Lineup;
};

export const FixtureTimeline = ({ so5Fixture, so5Lineup }: Props) => {
  const games = sortBy(
    game => game?.date || '',
    uniqueBy(
      game => game?.id || '',
      so5Lineup?.so5Appearances.map(appearance => appearance.game) || []
    )
  ).filter(Boolean);

  const allDates = [
    so5Fixture.startDate,
    ...games.map(game => game.date),
    so5Fixture.endDate,
    addHours(so5Fixture.endDate, 4),
  ];

  const nowPosition = allDates.findIndex(date => isFuture(date));
  const nowTimeline = (
    <TimelineRow
      label={
        <NowLabel>
          <FormattedMessage {...glossary.now} />
        </NowLabel>
      }
      extraInfo={
        isBefore(new Date(), so5Fixture.startDate) && (
          <FormattedMessage
            id="FixtureTimeline.canEdit"
            defaultMessage="You can still edit your lineup"
          />
        )
      }
    />
  );

  return (
    <Wrapper>
      <TimelineRowWithNow
        isRightAfterNow={nowPosition === 0}
        nowTimeline={nowTimeline}
        date={so5Fixture.startDate}
        label={
          <FormattedMessage
            id="FixtureStatus.gameweekStarts"
            defaultMessage="Gameweek starts"
          />
        }
      />
      {games.map((game, index) => (
        <TimelineRowWithNow
          isGameRow
          key={game.id}
          date={game.date}
          isRightAfterNow={nowPosition === index + 1}
          nowTimeline={nowTimeline}
          label={`${game.homeTeam?.code}-${game.awayTeam?.code}`}
          extraInfo={
            <GameWrapper>
              <InlineGame game={game} disableTooltip size="md" />
            </GameWrapper>
          }
        />
      ))}
      <TimelineRowWithNow
        isRightAfterNow={nowPosition === allDates.length - 2}
        nowTimeline={nowTimeline}
        date={so5Fixture.endDate}
        label={
          <FormattedMessage
            id="FixtureTimeline.gameweekCloses"
            defaultMessage="Gameweek closes"
          />
        }
      />
      <TimelineRowWithNow
        isRightAfterNow={nowPosition === allDates.length - 1}
        nowTimeline={nowTimeline}
        date={addHours(so5Fixture.endDate, 4)}
        label={
          <FormattedMessage
            id="FixtureTimeline.rewardsDistributed"
            defaultMessage="Rewards distributed"
          />
        }
      />
    </Wrapper>
  );
};

FixtureTimeline.fragments = {
  so5Fixture: gql`
    fragment FixtureTimeline_so5Fixture on So5Fixture {
      slug
      startDate
      endDate
    }
  ` as TypedDocumentNode<FixtureTimeline_so5Fixture>,
  so5Lineup: gql`
    fragment FixtureTimeline_so5Lineup on So5Lineup {
      id
      so5Appearances {
        id
        game {
          id
          date
          homeTeam {
            slug
            code
          }
          awayTeam {
            slug
            code
          }
          ...InlineGame_game
        }
      }
    }
    ${InlineGame.fragments.game}
  ` as TypedDocumentNode<FixtureTimeline_so5Lineup>,
};
